import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function PlumsteadPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Plumstead Painters Cape Town | Professional Painting Services | Family Home Specialists";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Plumstead properties. Expert family home painting, residential services, interior & exterior painting. Serving Plumstead Cape Town with premium quality finishes and family-friendly service.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Plumstead painters, family home painting, residential painting, Plumstead property painting, professional painters Cape Town');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/plumstead`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Plumstead Painters - Professional Painting Services Cape Town",
      "description": "Professional painting services for Plumstead properties with family home expertise and residential specialization.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/plumstead`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Plumstead",
        "description": "Specialized painting services for Plumstead's family homes and residential properties.",
        "areaServed": "Plumstead, Cape Town",
        "serviceType": "Family Home Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Plumstead')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Family Home Painting',
      description: 'Complete painting services for Plumstead family homes, working around busy schedules with minimal disruption to daily routines and child-safe materials.',
      price: 'From R10,000',
      features: ['Flexible scheduling', 'Child-safe paints', 'Quick turnaround', 'Minimal disruption']
    },
    {
      icon: 'ri-building-line',
      title: 'Residential Properties',
      description: 'Professional painting for all Plumstead residential property types from single-family homes to townhouse complexes with coordinated service.',
      price: 'From R8,000',
      features: ['All property types', 'Coordinated service', 'Quality materials', 'Professional finish']
    },
    {
      icon: 'ri-brush-line',
      title: 'Interior Painting',
      description: 'Expert interior painting services for Plumstead homes including living areas, bedrooms, kitchens, and bathrooms with attention to detail.',
      price: 'From R6,000',
      features: ['Room preparation', 'Premium paints', 'Color consultation', 'Clean finish']
    },
    {
      icon: 'ri-palette-line',
      title: 'Exterior Painting',
      description: 'Professional exterior painting protecting Plumstead homes from weather while enhancing curb appeal and maintaining property value.',
      price: 'From R9,000',
      features: ['Weather protection', 'Surface preparation', 'Durable coatings', 'Color matching']
    },
    {
      icon: 'ri-tools-line',
      title: 'Renovation Painting',
      description: 'Specialized painting services for Plumstead home renovations and upgrades, coordinating with other trades for seamless results.',
      price: 'From R7,000',
      features: ['Renovation coordination', 'Trade integration', 'Timeline management', 'Quality assurance']
    },
    {
      icon: 'ri-shield-line',
      title: 'Maintenance Programs',
      description: 'Regular maintenance painting services to protect your Plumstead property investment with scheduled inspections and preventive care.',
      price: 'From R4,000',
      features: ['Scheduled visits', 'Preventive maintenance', 'Priority service', 'Cost savings']
    }
  ];

  const testimonials = [
    {
      name: 'Lisa Johnson',
      property: 'Family Home',
      rating: 5,
      text: 'Excellent service! They worked around our family schedule and the kids loved watching the transformation. The house looks amazing and the team was so professional.',
      image: 'https://public.readdy.ai/ai/img_res/6df0a6150ddbe64aa7da8c6b700eaccb.jpg'
    },
    {
      name: 'Mark Stevens',
      property: 'Townhouse',
      rating: 5,
      text: 'Professional team with great attention to detail. They completed our Plumstead townhouse painting on time and within budget. Highly recommend their services.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20man%20homeowner%20at%20Plumstead%20townhouse%20with%20painted%20exterior%2C%20satisfied%20client%20portrait%2C%20residential%20property&width=80&height=80&seq=plumstead2&orientation=squarish'
    },
    {
      name: 'Catherine Brown',
      property: 'Residential Home',
      rating: 5,
      text: 'Outstanding work on both interior and exterior. The quality is excellent and they were very respectful of our home. The results exceeded our expectations.',
      image: 'https://readdy.ai/api/search-image?query=Satisfied%20woman%20homeowner%20in%20Plumstead%20residential%20home%20with%20newly%20painted%20rooms%2C%20happy%20customer%20portrait%2C%20modern%20interior&width=80&height=80&seq=plumstead5&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Family Home Transformation',
      description: 'Complete interior and exterior painting for Plumstead family home with modern color palette and durable finishes',
      image: 'https://readdy.ai/api/search-image?query=Plumstead%20family%20home%20with%20freshly%20painted%20exterior%2C%20modern%20colors%2C%20professional%20painting%20work%2C%20residential%20property&width=400&height=300&seq=plumstead3&orientation=landscape'
    },
    {
      title: 'Townhouse Complex',
      description: 'Multi-unit townhouse painting project with coordinated colors and efficient scheduling for minimal resident disruption',
      image: 'https://readdy.ai/api/search-image?query=Plumstead%20townhouse%20complex%20with%20newly%20painted%20exteriors%2C%20coordinated%20colors%2C%20professional%20maintenance%20work&width=400&height=300&seq=plumstead4&orientation=landscape'
    },
    {
      title: 'Renovation Project',
      description: 'Complete painting service for Plumstead home renovation including interior updates and exterior refresh',
      image: 'https://readdy.ai/api/search-image?query=Plumstead%20home%20renovation%20with%20fresh%20paint%2C%20modern%20updates%2C%20professional%20painting%20finish%2C%20residential%20property&width=400&height=300&seq=plumstead6&orientation=landscape'
    },
    {
      title: 'Heritage Home Update',
      description: 'Careful painting of older Plumstead property preserving character while providing modern protection and finishes',
      image: 'https://readdy.ai/api/search-image?query=Heritage%20home%20in%20Plumstead%20with%20updated%20painted%20exterior%2C%20period%20features%2C%20professional%20restoration%20work&width=400&height=300&seq=plumstead7&orientation=landscape'
    }
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Family Consultation',
      description: 'Comprehensive consultation understanding your Plumstead family\'s needs, schedule requirements, and specific preferences for the project.'
    },
    {
      step: '2',
      title: 'Flexible Planning',
      description: 'Development of painting schedule that works around your family routine with minimal disruption to daily activities and school schedules.'
    },
    {
      step: '3',
      title: 'Safe Preparation',
      description: 'Careful preparation with family safety as priority, using child-safe materials and securing work areas appropriately.'
    },
    {
      step: '4',
      title: 'Professional Application',
      description: 'Expert painting using low-VOC, family-friendly paints with efficient techniques for quick completion and minimal odor.'
    },
    {
      step: '5',
      title: 'Quality Inspection',
      description: 'Thorough inspection ensuring every detail meets our high standards and your family\'s expectations for the finished result.'
    },
    {
      step: '6',
      title: 'Family Guidance',
      description: 'Comprehensive care instructions and maintenance tips specifically tailored for busy Plumstead families with children.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20family%20home%20in%20Plumstead%20Cape%20Town%2C%20residential%20painting%2C%20painting%20equipment%2C%20beautiful%20suburban%20property%2C%20sunny%20day&width=1920&height=1080&seq=plumsteadhero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Plumstead
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Plumstead family homes and residential properties. Specialized family-friendly painting with flexible scheduling and child-safe materials for your home with minimal disruption to your daily routine.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Plumstead Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Family-Focused Painting Services in Plumstead
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Plumstead is a well-established residential suburb in Cape Town's Southern Suburbs, known for its family-friendly atmosphere, excellent schools, and strong community spirit. This popular area attracts young families and professionals who appreciate the combination of suburban tranquility, convenient amenities, and easy access to major transport routes including the M3 and Southern Line railway. The suburb features a diverse mix of property types from charming older homes to modern developments, creating a vibrant and welcoming community.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The residential character of Plumstead presents unique opportunities for painting services that understand family needs and busy household schedules. Many properties in the area are family homes with active children, requiring painting solutions that minimize disruption while ensuring safety and quality. The suburb's established nature means many homes benefit from regular maintenance and updates, while newer developments require quality finishes that will stand the test of time and family life.
              </p>
              <p className="text-lg text-gray-600">
                Our team specializes in working with Plumstead families, understanding the importance of flexible scheduling, child-safe materials, and efficient project completion. We pride ourselves on being respectful of your home and family routine, working around school schedules and daily activities to minimize disruption. Whether you need a complete home transformation or targeted room updates, we provide professional service that puts your family's needs first while delivering exceptional results that enhance your home's value and appeal.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Plumstead%20Cape%20Town%20residential%20street%20with%20family%20homes%2C%20suburban%20setting%2C%20tree-lined%20roads%2C%20established%20neighborhood&width=600&height=400&seq=plumstead8&orientation=landscape"
                alt="Plumstead Residential Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Plumstead Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed specifically for Plumstead families and residential properties with flexible, family-friendly service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Plumstead Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A family-focused approach ensuring exceptional results with minimal disruption to your Plumstead household.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Plumstead */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Plumstead?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in family home painting with proven results and satisfied Plumstead families.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-parent-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Family Specialists</h3>
              <p className="text-gray-600">Extensive experience working with Plumstead families, understanding the unique needs of households with children and busy schedules.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Child-Safe Materials</h3>
              <p className="text-gray-600">Exclusive use of low-VOC, child-safe paints and materials ensuring your family\'s health and safety throughout the project.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-time-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Flexible Scheduling</h3>
              <p className="text-gray-600">Accommodating work schedules that fit around your family routine, school hours, and daily activities for minimal disruption.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-customer-service-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Respectful Service</h3>
              <p className="text-gray-600">Professional team that respects your home and family, maintaining clean work areas and communicating clearly throughout the project.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Plumstead Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in Plumstead family home and residential painting with recent successful projects across the area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Plumstead Family Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Plumstead families who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Family Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Family-Friendly Painting Benefits
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Plumstead Families Choose Us</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-heart-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Health & Safety Priority</h4>
                    <p className="text-gray-600">We use only low-VOC, environmentally friendly paints that are safe for children and pets, ensuring your family\'s health is never compromised during or after the painting process.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-calendar-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Flexible Timing</h4>
                    <p className="text-gray-600">We work around your family schedule, including school hours and after-school activities, ensuring minimal disruption to your daily routine and children\'s schedules.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-speed-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Efficient Completion</h4>
                    <p className="text-gray-600">Our experienced team works efficiently to complete projects quickly without compromising quality, minimizing the time your home is in a state of renovation.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-home-heart-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Respectful Presence</h4>
                    <p className="text-gray-600">Our professional team maintains clean work areas, respects your family\'s privacy and space, and communicates clearly about daily progress and any considerations.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Happy%20family%20in%20Plumstead%20home%20with%20freshly%20painted%20rooms%2C%20children%20playing%2C%20modern%20interior%2C%20family-friendly%20environment&width=600&height=500&seq=plumstead9&orientation=portrait"
                alt="Plumstead Family Home"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Plumstead Home?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's family painting specialists for Plumstead. Our expert team understands the unique needs of busy families and is ready to provide you with a comprehensive consultation and detailed quote. Experience the difference that family-focused painting expertise makes for your Plumstead home with flexible scheduling and child-safe materials.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
